import { Button, Flex, Typography } from "antd";

import React, { ReactNode, useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import '../animations.css'; // Import global animations (used elsewhere)
import backgroundDNA from '../images/background-dna.jpg';
import homeDoublePeak from '../images/homeDoublePeak.png';
import homeNewPlasmid from '../images/homeNewPlasmid.png';
import backgroundVideo from '../images/homepagebackgroundvideov2_compressed.mp4';
import nanoporePicture from '../images/nanopore-image.png';

const GradientButton = styled(Button)`
  margin-top: 0.5em;
  font-size: 2rem;
  padding: 1.5rem;

  /* initial gradient & turquoise text */
  background: ${({ theme }) => theme.background};
    
  color: ${({ theme }) => theme.primary};
  border: 2px solid ${({ theme }) => theme.primary};

  transition: filter 0.3s ease, color 0.3s ease, border-color 0.3s ease;

  &:hover,
  &:focus,
  &:active {
    /* reverse the gradient */
    background: linear-gradient(
      90deg,
      ${({ theme }) => theme.primaryLight},
      ${({ theme }) => theme.primary}
    );
    /* darken everything inside */
    filter: brightness(0.8);
    color: ${({ theme }) => theme.white};
    border-color: ${({ theme }) => theme.white};
  }
`;

/* Ticker styled without CSS animation */
const PricingBanner = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.primaryLight}, ${({ theme }) => theme.primary});
  color: ${({ theme }) => theme.white};
  padding: 2rem 1rem;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  > div {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: clamp(1rem, 4vw, 4rem);
  }
  .text-item {
    display: flex;
    align-items: center;
    gap: clamp(0.5rem, 2vw, 1rem);
    font-size: clamp(1.25rem, 3vw, 2rem);
    font-weight: bold;
  }
  .icon {
    font-size: clamp(3rem, 6vw, 5rem);
    flex-shrink: 0;
    color: ${({ theme }) => theme.white};
  }
  .delivery-note {
    font-size: clamp(0.75rem, 1.5vw, 1rem);
    font-style: italic;
    font-weight: normal;
    opacity: 0.9;
    margin-top: clamp(0.25rem, 1vw, 0.5rem);
  }
`;

const MobileResponsiveContainer = styled.div`
  @media (max-width: 900px) {
  h1 {
    margin-bottom: -1rem !important;
  }
    .nanopore-section {
      flex-direction: column !important;
      justify-content: center !important;
      align-items: center !important;
      
      .nanopore-image {
        order: 1;
        margin-bottom: 1rem;
        
        img {
          height: 40vw !important;
          width: auto !important;
        }
      }
      
      .nanopore-text {
        order: 2;
        text-align: center;
        
        h2 {
          font-size: 2rem !important;
          margin-bottom: -1rem !important;
        }
        
        button {
          font-size: 1.5rem !important;
          padding: 1rem !important;
        }
      }
    }
    
    .consensus-section {
      flex-direction: column !important;
      
      .consensus-text {
        order: 2;
        text-align: center;
        
        h2 {
          font-size: 2rem !important;
          margin-bottom: -1rem !important;
        }
        
        button {
          font-size: 1.5rem !important;
          padding: 1rem !important;
        }
      }
      
      .consensus-images {
        order: 1;
        flex-direction: row !important;
        justify-content: center !important;
        gap: 0rem !important;
        margin-bottom: 1rem;
        height: auto !important;
        max-height: none !important;
        
        img {
          width: 45vw !important;
          height: 35vw !important;
          object-fit: contain !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          background: transparent !important;
        }
      }
    }
    
    /* Hero section mobile adjustments */
    .hero-title {
      font-size: 3rem !important;
    }
    
    .hero-subtitle {
      font-size: 1.2rem !important;
    }
    
    .hero-button {
      font-size: 2rem !important;
      padding: 1.5rem !important;
    }
    
    /* Final section mobile adjustments */
    .final-text {
      font-size: 1.5rem !important;
    }
    
    .final-button {
      font-size: 2rem !important;
      padding: 1.5rem !important;
    }
  }
`;

function DemoBox({ children, bg, color, videoSrc, imageSrc }: { bg?: string; color?: string; videoSrc?: string; imageSrc?: string; children: ReactNode }): JSX.Element {
//   console.log('DemoBox videoSrc:', videoSrc)
  return (
    <Flex
      justify="center"
      align="center"
      style={{
        height: '24em',
        fontSize: '2rem',
        position: 'relative',
        overflow: 'hidden',
        color,
        backgroundColor: bg,

      }}
    >
            {/* Optional overlay to dim the image background */}
      {imageSrc && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            opacity: '0.15', // adjust opacity here
            backgroundImage: `url(${imageSrc})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            zIndex: 0,
          }}
        />
      )}

      {videoSrc && (
        <video
          autoPlay
          loop
          muted
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        >

          <source src={videoSrc} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      )}
      <div style={{ position: 'relative', zIndex: 1 }}>{children}</div>
    </Flex>
  )
}

export function Home(): JSX.Element {
  const theme = useTheme();
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisibleConsensus, setIsVisibleConsensus] = useState(false);
  const containerRefConsensus = useRef<HTMLDivElement>(null);
  const [isVisibleEnd, setIsVisibleEnd] = useState(false);
  const containerRefEnd = useRef<HTMLDivElement>(null);
  const fullText =
    "We're the first to offer quality-controlled sequencing, so you can make the most-informed decisions.";
  const [displayedText, setDisplayedText] = useState('');
  // Pricing banner visibility
  const [isVisiblePricing, setIsVisiblePricing] = useState(false);
  const containerRefPricing = useRef<HTMLDivElement>(null);

  // FIRST
  useEffect(() => {
    if (!containerRef.current) return

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          obs.unobserve(entry.target)      // only trigger once
        }
      },
      { threshold: 0.25 }                  // fire when 25% in view
    )

    obs.observe(containerRef.current)

    return () => {
      obs.disconnect()
    }
  }, [isVisiblePricing])

  // Consensus Section

    useEffect(() => {
    if (!containerRefConsensus.current) return

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisibleConsensus(true)
          obs.unobserve(entry.target)      // only trigger once
        }
      },
      { threshold: 0.25 }                  // fire when 25% in view
    )

    obs.observe(containerRefConsensus.current)

    return () => {
      obs.disconnect()
    }
  }, [])

  // END

    useEffect(() => {
    if (!containerRefEnd.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisibleEnd(true);
          obs.unobserve(entry.target);
        }
      },
      { threshold: 0.25 }
    );
    obs.observe(containerRefEnd.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisibleEnd) return;
    let idx = 0;
    const interval = setInterval(() => {
      setDisplayedText(fullText.slice(0, idx + 1));
      idx++;
      if (idx >= fullText.length) {
        clearInterval(interval);
      }
    }, 30); // tweak speed here (ms per character)
    return () => clearInterval(interval);
  }, [isVisibleEnd, fullText]);

  // Pricing banner slide-in observer
  useEffect(() => {
    if (!containerRefPricing.current) return;
    const obsPricing = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisiblePricing(true);
          obsPricing.unobserve(entry.target);
        }
      },
      { threshold: 0.25 }
    );
    obsPricing.observe(containerRefPricing.current);
    return () => obsPricing.disconnect();
  }, []);

  const renderTextWithBold = (text: string, boldPhrases: string[]) => {
    const regex = new RegExp(`(${boldPhrases.join('|')})`, 'gi'); // Match all phrases, case-insensitive
    const parts = text.split(regex);

    return (
      <>
        {parts.map((part, index) =>
          boldPhrases.some(
            phrase => part.toLowerCase() === phrase.toLowerCase()
          ) ? (
            <strong key={index} style={{ fontWeight: 'bold' }}>
              {part}
            </strong>
          ) : (
            <React.Fragment key={index}>{part}</React.Fragment>
          )
        )}
      </>
    );
  };


    // 3️⃣ Only show the button once typing is done
  const typingDone = displayedText === fullText;
  

  return (
    <MobileResponsiveContainer>

<DemoBox
  bg={theme.primary}
  color={theme.white}
  videoSrc={backgroundVideo}
>
  <Typography style={{ textAlign: 'center' }}>
    {/* Big, bold headline */}
    <h1
      className="hero-title"
      style={{
        color: theme.white,
        fontWeight: 'bold',
        fontSize: '4.5rem',    // ~2× the usual h1 size
        marginBottom: '-1.5rem',
        lineHeight: 1.2,
      }}
    >
      More Than Just Sequencing
    </h1>

    {/* Smaller byline */}
    <p
      className="hero-subtitle"
      style={{
        display: 'block',
        color: theme.white,
        fontSize: '1.5rem',
        marginBottom: '0.5em',
        opacity: 0.85,
      }}
    >
      Get the full quality assessment based on the latest Nanopore technology.
    </p>

    {/* Primary button linking to /order */}
    <Button type="primary" size="large"
      className="hero-button"      
      style={{
        fontSize: '3rem',      // 2× default text
        padding: '2rem',  // more click area
      }}>
      <NavLink to="/order" style={{ color: theme.white }}>
        Order Now
      </NavLink>
    </Button>
  </Typography>
</DemoBox>




      
  <DemoBox imageSrc={backgroundDNA}>
        <div
          ref={containerRef}
          className="nanopore-section"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {/* Left Side: Image */}
          <div
            className={`hidden nanopore-image ${isVisible ? 'slide-in-left' : ''}`}
            style={{ flex: 1, padding: '1rem' }}
          >
            <img
              src={nanoporePicture}
              alt="Nanopore Sequencing"
              style={{
                width: '100%',
                height: '12em',
                objectFit: 'cover',
                borderRadius: '8px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.5)',
              }}
            />
          </div>

          {/* Right Side: Text */}
          <div
            className={`hidden nanopore-text ${isVisible ? 'slide-in-right' : ''}`}
            style={{ flex: 1, padding: '1rem' }}
          >
            
            <p
              style={{
                fontSize: '2rem',
                opacity: 0.85,
                lineHeight: 1.5,
                display: 'block',
                marginBottom: '0.5em',
              }}
            >
              Single molecule sequencing without the need for DNA polymerases.
            </p>
            

            <Button
              type="primary"
              size="large"
              style={{ marginTop: '0.5em', fontSize: '2rem', padding: '1.5rem' }}
            >
              <NavLink to="/order" style={{ color: theme.white }}>
                Order Nanopore Sequencing
              </NavLink>
            </Button>
          </div>
        </div>
    </DemoBox>

{/* Pricing and Delivery Banner */}
{/* <PricingBanner ref={containerRefPricing}>
  <div>
    <div className={`text-item ${isVisiblePricing ? 'slide-in-left' : 'hidden'}`}>
      <FaDna className="icon" />
      <span>$16/Sample</span>
    </div>
    <div className={`text-item ${isVisiblePricing ? 'slide-in-right' : 'hidden'}`}>
      <FaTruck className="icon" />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <span>Next-Day Results</span>
      </div>
    </div>
  </div>
</PricingBanner> */}

<DemoBox 
  bg={theme.primary}
  color={theme.white}>
  <div
    ref={containerRefConsensus}
    className="consensus-section"
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%'
    }}
  >
    {/* Left Side: Text */}
    <div
      className={`hidden consensus-text ${isVisibleConsensus ? 'slide-in-left' : ''}`}
      style={{ flex: 1, padding: '1rem' }}
    >
      <h2
        style={{
          fontSize: '3rem',
          margin: 0,
          color: theme.white,
        }}
      >
        More Than One Consensus
      </h2>
      <p
        style={{
          fontSize: '1.5rem',
          opacity: 0.85,
          lineHeight: 1.5,
          display: 'block',
          marginBottom: '0.5em',
          color: theme.white,
        }}
      >
        PLab Sequencing answers your scientific questions by providing you with the full picture for <i>every sample.</i> 
      </p>
      <GradientButton>
        <NavLink
          to="/order"
          style={{
            color: 'inherit',
            textDecoration: 'none',
          }}
        >
          Order the Full Picture
        </NavLink>
      </GradientButton>
    </div>

    {/* Right Side: Two stacked images */}
    <div
      className={`hidden consensus-images ${isVisibleConsensus ? 'slide-in-right' : ''}`}
      style={{ flex: 1, padding: 0, display: 'flex', flexDirection: 'column', gap: '1.5rem', justifyContent: 'center', height: '22em', maxHeight: '22em', alignItems: 'center' }}
    >
      <img
        src={homeNewPlasmid}
        alt="Plasmid Example"
        style={{
          width: '12em',
          height: 'auto',
          maxHeight: '100%',
          objectFit: 'contain',
          borderRadius: '12px', // add rounded corners
          boxShadow: 'none',
          background: 'transparent',
          margin: 0,
          display: 'block',
        }}
      />
      <img
        src={homeDoublePeak}
        alt="Double Peak Example"
        style={{
          width: '12em',
          height: 'auto',
          maxHeight: '100%',
          objectFit: 'contain',
          borderRadius: '12px', // add rounded corners
          boxShadow: 'none',
          background: 'transparent',
          margin: 0,
          display: 'block',
        }}
      />
    </div>
  </div>
</DemoBox>

<DemoBox>
  <div ref={containerRefEnd}>
      <Typography style={{ textAlign: 'center' }}>
        <Typography.Text
          className="final-text"
          style={{
            display: 'block',
            color: theme.primary,
            fontSize: '2rem',
            marginBottom: '1em',
            whiteSpace: 'pre-wrap',   // preserve spaces
          }}
        >
          {renderTextWithBold(displayedText, ["quality-controlled sequencing"])}
        </Typography.Text>

        <Button
          type="primary"
          size="large"
          className="final-button"
          style={{
            fontSize: '3rem',
            padding: '2rem',
            opacity: typingDone ? 1 : 0,
            transition: 'opacity 2s ease',
          }}
        >
          <NavLink to="/order" style={{ color: theme.white }}>
            Order Now
          </NavLink>
        </Button>
      </Typography>
      </div>
    </DemoBox>
    </MobileResponsiveContainer>
  )
}
